import { ArrowLeft, ArrowRight, Bot, ExternalLink, Globe2, LoaderCircle, Pause, Play, Plus, Power, RefreshCw, Settings2, X } from "lucide-react"
import NiceModal from "@ebay/nice-modal-react"
import { observer } from "mobx-react"
import { useEffect, useRef, useState } from "react"
import { embeddedBrowserStore } from "../../browser/store"
import { normalizeManualBrowserInput } from "../../browser/browserUrl"
import { openUrlInNativeBrowser } from "../../electronAPIs/shell"
import type { BrowserSurface } from "../../browser/tab"
import { BrowserWebview } from "./BrowserWebview"
import { BrowserUserActionBanner } from "./BrowserUserActionBanner"
import { BrowserCertificateWarning } from "./BrowserCertificateWarning"
import { SettingsModal } from "../settings/SettingsModal"
import { useCodeStore } from "../../store/context"

const iconButton =
    "btn flex h-8 w-8 shrink-0 items-center justify-center border border-transparent text-muted transition-colors hover:border-border hover:bg-base-200 hover:text-base-content disabled:cursor-not-allowed disabled:opacity-35"

export const BrowserPanel = observer(function BrowserPanel(): JSX.Element {
    const codeStore = useCodeStore()
    const store = embeddedBrowserStore
    const activeTab = store.activeTab
    const visibleTabHasAgent = activeTab?.ownerSessionId !== null && activeTab?.ownerSessionId !== undefined
    const visibleTabAgentAction = store.visibleTabAgentAction
    const visibleTabAgentPaused = store.visibleTabAgentPaused
    const visibleTabNeedsUserAction = store.visibleTabUserAction !== null
    const controlPaused = store.globalAgentPaused || visibleTabAgentPaused
    const [address, setAddress] = useState(activeTab?.url === "about:blank" ? "" : (activeTab?.url ?? ""))
    const addressRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        void store.initialize().catch(() => store.setNotice("This desktop build cannot start Browser."))
    }, [store])

    useEffect(() => {
        const targetUrl = activeTab?.certificateError?.url ?? activeTab?.url
        setAddress(targetUrl === "about:blank" ? "" : (targetUrl ?? ""))
    }, [activeTab?.certificateError?.url, activeTab?.id, activeTab?.url])

    useEffect(() => {
        if (store.addressFocusGeneration === 0) return
        addressRef.current?.focus()
        addressRef.current?.select()
    }, [store.addressFocusGeneration])

    const navigate = async () => {
        if (!activeTab || address.trim().length === 0) return
        const url = normalizeManualBrowserInput(address)
        setAddress(url)
        store.setNotice(null)
        try {
            await store.navigate(activeTab.id, url)
        } catch (error) {
            store.setNotice(
                error instanceof Error && error.message === "browser_unsafe_url"
                    ? "That address uses an unsupported protocol."
                    : "Browser could not load that page."
            )
        }
    }

    const surfaceAction = (action: (surface: BrowserSurface) => void) => {
        if (!activeTab) return
        void store
            .surface(activeTab.id)
            .then(action)
            .catch(() => store.setNotice("Browser could not attach to this tab."))
    }

    return (
        <section className="flex h-full min-h-0 min-w-0 flex-col bg-base-100" aria-label="Embedded Browser workspace">
            <div
                className="flex h-10 shrink-0 items-end gap-1 overflow-hidden border-b border-border bg-base-200/55 px-2 pt-1"
                role="tablist"
                aria-label="Browser tabs"
            >
                {store.tabs.map((tab) => {
                    const active = tab.id === store.activeTabId
                    const needsUserAction = store.pendingUserActions.has(tab.id)
                    return (
                        <div
                            key={tab.id}
                            className={`flex h-8 min-w-10 max-w-44 flex-1 items-center gap-1 overflow-hidden border border-b-0 px-2 ${active ? "border-border bg-base-100 text-base-content" : "border-transparent text-muted hover:bg-base-200"}`}
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected={active}
                                aria-label={`${tab.title || "New tab"}${needsUserAction ? " · Needs your help" : ""}`}
                                className="btn flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs"
                                onClick={() => store.selectTab(tab.id)}
                                title={`${tab.title}${tab.ownerSessionId ? " · Agent session tab" : ""}`}
                            >
                                {needsUserAction ? (
                                    <Bot size={12} className="shrink-0 text-warning" />
                                ) : tab.loading ? (
                                    <LoaderCircle size={12} className="shrink-0 animate-spin" />
                                ) : (
                                    <Globe2 size={12} className="shrink-0" />
                                )}
                                <span className="truncate">{tab.title || "New tab"}</span>
                            </button>
                            {active && (
                                <button
                                    type="button"
                                    className="btn shrink-0 text-muted hover:text-base-content"
                                    aria-label={`Close ${tab.title}`}
                                    onClick={() => store.closeTab(tab.id)}
                                >
                                    <X size={11} />
                                </button>
                            )}
                        </div>
                    )
                })}
                <button
                    type="button"
                    className={iconButton}
                    aria-label="Manage embedded Browser"
                    title="Browser settings"
                    onClick={() =>
                        void NiceModal.show(SettingsModal, {
                            store: codeStore,
                            initialTab: "browser",
                        })
                    }
                >
                    <Settings2 size={14} />
                </button>
                <button
                    type="button"
                    className={`${iconButton} mb-0.5 h-7 w-7`}
                    disabled={store.tabs.length >= 8}
                    aria-label="New browser tab"
                    title="New tab"
                    onClick={() => store.createTab()}
                >
                    <Plus size={14} />
                </button>
            </div>

            <div className="flex h-11 shrink-0 items-center gap-1 border-b border-border bg-base-100 px-2">
                <button
                    type="button"
                    className={iconButton}
                    disabled={!activeTab?.canGoBack}
                    aria-label="Back"
                    title="Back"
                    onClick={() => surfaceAction((surface) => surface.goBack())}
                >
                    <ArrowLeft size={15} />
                </button>
                <button
                    type="button"
                    className={iconButton}
                    disabled={!activeTab?.canGoForward}
                    aria-label="Forward"
                    title="Forward"
                    onClick={() => surfaceAction((surface) => surface.goForward())}
                >
                    <ArrowRight size={15} />
                </button>
                <button
                    type="button"
                    className={iconButton}
                    disabled={!activeTab}
                    aria-label={activeTab?.loading ? "Stop" : "Reload"}
                    title={activeTab?.loading ? "Stop" : "Reload"}
                    onClick={() => surfaceAction(activeTab?.loading ? (surface) => surface.stop() : (surface) => surface.reload())}
                >
                    {activeTab?.loading ? <X size={15} /> : <RefreshCw size={15} />}
                </button>
                <form
                    className="min-w-0 flex-1"
                    onSubmit={(event) => {
                        event.preventDefault()
                        void navigate()
                    }}
                >
                    <input
                        ref={addressRef}
                        className="h-8 w-full min-w-0 border border-border bg-base-200 px-2 text-xs text-base-content outline-none focus:border-primary"
                        value={address}
                        placeholder="Search or enter a URL"
                        aria-label="Browser address"
                        spellCheck={false}
                        onChange={(event) => setAddress(event.target.value)}
                        onFocus={(event) => event.currentTarget.select()}
                    />
                </form>
                <div
                    className={`flex h-8 shrink-0 items-center gap-1.5 border px-2 text-[10px] font-medium ${controlPaused ? "border-warning/40 bg-warning/10 text-base-content" : visibleTabAgentAction || store.activeAgentCount > 0 ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted"}`}
                    title={
                        store.globalAgentPaused
                            ? "Agent Browser control is paused globally after a reset"
                            : visibleTabAgentPaused
                              ? "You paused agent control for this Browser tab"
                              : visibleTabAgentAction
                                ? `This tab's agent is running ${visibleTabAgentAction}`
                                : store.activeAgentCount > 0
                                  ? `${store.activeAgentCount} agent${store.activeAgentCount === 1 ? " is" : "s are"} active on other Browser tabs`
                                  : "This Browser tab's agent is ready"
                    }
                >
                    <Bot size={12} />
                    <span className="hidden min-[980px]:inline">
                        {store.globalAgentPaused
                            ? "All paused"
                            : visibleTabAgentPaused
                              ? "You control"
                              : visibleTabAgentAction
                                ? "Agent active"
                                : store.activeAgentCount > 0
                                  ? `${store.activeAgentCount} active`
                                  : "Agent ready"}
                    </span>
                </div>
                <button
                    type="button"
                    className={iconButton}
                    disabled={visibleTabNeedsUserAction || (!visibleTabHasAgent && !store.globalAgentPaused)}
                    aria-label={
                        visibleTabNeedsUserAction
                            ? "Finish the requested Browser step before resuming the agent"
                            : store.globalAgentPaused
                              ? "Resume agent control globally after the Browser reset"
                              : !visibleTabHasAgent
                                ? "This Browser tab is not controlled by an agent"
                                : visibleTabAgentPaused
                                  ? "Resume agent control for this Browser tab"
                                  : "Take over this Browser tab from its agent"
                    }
                    title={
                        visibleTabNeedsUserAction
                            ? "Finish the requested step below"
                            : store.globalAgentPaused
                              ? "Resume all Browser agents after reset"
                              : !visibleTabHasAgent
                                ? "This tab is not controlled by an agent"
                                : visibleTabAgentPaused
                                  ? "Resume this tab's agent"
                                  : "Take over this tab"
                    }
                    onClick={() => (store.globalAgentPaused ? store.resumeGlobalAgentControl() : store.setAgentPaused(!visibleTabAgentPaused))}
                >
                    {controlPaused ? <Play size={14} /> : <Pause size={14} />}
                </button>
                <button
                    type="button"
                    className={iconButton}
                    disabled={!activeTab || activeTab.url === "about:blank"}
                    aria-label="Open in system browser"
                    title="Open in system browser"
                    onClick={() => activeTab && openUrlInNativeBrowser(activeTab.url)}
                >
                    <ExternalLink size={14} />
                </button>
                <button
                    type="button"
                    className={iconButton}
                    aria-label="Stop Browser session"
                    title="Stop Browser session"
                    onClick={() => {
                        if (!window.confirm("Stop this Browser session and close its tabs? Website sign-ins and site data will be kept.")) return
                        store.stopSession()
                    }}
                >
                    <Power size={14} />
                </button>
            </div>

            {[...store.pendingUserActions.values()].map((request) => (
                <div key={request.id} className="shrink-0" hidden={request.tabId !== activeTab?.id && store.tabs.some((tab) => tab.id === request.tabId)}>
                    <BrowserUserActionBanner
                        instruction={request.instruction}
                        status={request.status}
                        outcome={request.outcome}
                        error={request.error}
                        onComplete={(completed, userNotes) =>
                            void store.completeUserAction({ tabId: request.tabId, requestId: request.id, completed, userNotes })
                        }
                    />
                </div>
            ))}

            {store.visibleTabManualActionRequired && !visibleTabNeedsUserAction && (
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-warning/40 bg-warning/10 px-3 py-2 text-xs text-base-content">
                    <span>Manual action is required on this tab. Agent control stays paused until you resume it.</span>
                    <button
                        type="button"
                        className="btn h-8 bg-primary px-3 text-xs font-medium text-primary-content"
                        onClick={() => store.setAgentPaused(false)}
                    >
                        Resume this agent
                    </button>
                </div>
            )}

            {store.notice && (
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-warning/10 px-3 py-1.5 text-[11px] text-base-content">
                    <span>{store.notice}</span>
                    <button type="button" className="btn text-muted hover:text-base-content" onClick={() => store.setNotice(null)}>
                        Dismiss
                    </button>
                </div>
            )}

            <div className="relative min-h-0 flex-1 overflow-hidden bg-white">
                {store.partition &&
                    store.tabs.map((tab) => (
                        <BrowserWebview key={`${tab.id}:${tab.surfaceKey}`} tab={tab} partition={store.partition ?? ""} active={tab.id === store.activeTabId} />
                    ))}
                {activeTab?.url === "about:blank" && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-base-100">
                        <div className="flex flex-col items-center gap-1.5 text-center">
                            <Globe2 size={22} className="mb-1 text-muted" />
                            <p className="text-sm font-medium text-base-content">Start browsing</p>
                            <p className="text-xs text-muted">Enter a URL, or let the agent open one.</p>
                        </div>
                    </div>
                )}
                {activeTab?.certificateError && (
                    <BrowserCertificateWarning
                        key={activeTab.certificateError.challengeId}
                        warning={activeTab.certificateError}
                        onGoBack={() => store.leaveCertificateError(activeTab.id)}
                        onProceed={() => store.proceedCertificateError(activeTab.id)}
                    />
                )}
            </div>
        </section>
    )
})
